import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, renderSlot as _renderSlot, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["onSubmit"]
const _hoisted_2 = { class: "add-ident-form__buttons" }

import '@/components/common/AddIdent/style.scss';
import { INotificationsPlugin } from '@/plugins/notificationsPlugin/@types';
import { IAddAccountByIdentResult } from '@/@types/api/user'

import SmSelect from '@/components/ui/form/SmSelect/SmSelect.vue';
import SmInput from '@/components/ui/form/SmInput/SmInput.vue';
import SmButton from '@/components/ui/buttons/SmButton/SmButton.vue';

import { ref, reactive, computed, watch, inject } from 'vue';
import { useStore } from 'vuex';
import { useConfig } from '@/composables/config';
import { useUser } from '@/composables/useUser';
import { useRequests } from '@/composables/requests';
import { useI18n } from 'vue-i18n';

interface IForm {
  ident?: string| null;
  pinCode?: string| null;
  clientBaseID?: number | null;
  billAmount?: string | null;
  inn: string | null;
  lastName: string | null;
  houseId: number | null;
  premiseNumber: string | number | null;
}
// [END] Types and interfaces

// [START] Data

export default /*#__PURE__*/_defineComponent({
  __name: 'AddIdentForm',
  emits: ["addAccountByIdent"],
  setup(__props, { emit: emits }) {

const { addAccountByIdent, getClientBases, getAccountPrefixes } = useUser();
const { fetchHouseList, fetchHouse } = useRequests();
const { t } = useI18n();
const { createPush } = inject('notifications-plugin') as INotificationsPlugin;

// [START] Emits

// [END] Emits

// [START] Types and interfaces
const store = useStore();

const { getAppSettings } = useConfig();

const appSettings = getAppSettings();
const addAccountByIdentOptions = appSettings?.AddAccountByIdentOptions;

const prefixes = store.getters['user/getAccountPrefixes'];
const clientBases = store.getters['user/getClientBases'];

const form = reactive<IForm>({
  ident: null,
  pinCode: null,
  clientBaseID: null,
  billAmount: null,
  inn: null,
  lastName: null,
  houseId: null, 
  premiseNumber: null,
});
const stage = ref<number>(1);
const isLoadingAddAccountByIdent = ref<boolean>(false);
const selectedPrefix = ref<string>('');
const houses = ref([]);
const houseData = ref([]);
const isSending = ref(false);
const selectedAccount = ref<IAddAccountByIdentResult | null>(null);
const isLegalAccount = appSettings?.allowedAccountTypes === 1
// [END] Data

// [START] Computed
const isRostovZKH = computed((): boolean => {
  return window.appSettings?.apiUrl.split('/').includes('rostov-zkh') || false;
});

const codeFromBillMessage = computed((): string => isLegalAccount ? t('code_from_contract_bill_message') : t('code_from_bill_message'))
const sixDigitsCodeLabel = computed((): string => isLegalAccount ? t('six_digits_code_from_contract_bill_message') : t('six_digits_code_from_bill_message'))
const identHint = computed((): string => isLegalAccount ? t('adding_a_legal_account_hint') : t('adding_a_personal_account_hint'))
const identNumber = computed((): string => isLegalAccount ? t('contract_number') : t('personal_account_number'))
const addBillSumLabel = computed((): string => isLegalAccount ? t('add_contract_bill_sum') : t('add_ident_bill_sum'))

const promtPinCodeMessage = computed((): string => {
  return isRostovZKH.value
    ? sixDigitsCodeLabel.value
    : codeFromBillMessage.value;
});

const accessCodeFromBillLabel = computed((): string => isLegalAccount ? t('access_code_from_contract_bill') : t('access_code_from_bill'))

const promtPinCodeLabel = computed((): string => {
  const pinCodeFieldName = addAccountByIdentOptions.pinCodeFieldName;

  return isRostovZKH.value ? accessCodeFromBillLabel.value : pinCodeFieldName;
});

const promtPinCodeMaskOptions = computed((): string => {
  return isRostovZKH.value ? '######' : '####';
});

const isDisabledButton = computed((): boolean => {
  const { ident, pinCode, billAmount, inn, lastName, houseId, premiseNumber } = form;
  const promtBillAmount = addAccountByIdentOptions.promtBillAmount;
  const isValidHouseData = houseData.value.length > 0 ? !!premiseNumber : true;
  const isValidUserCredentials = inn ? inn : lastName;
  const isValid = (isValidUserCredentials && houseId && isValidHouseData) || ident?.length;

  if (stage.value === 1) {
    if (!isValid) return true;
    if (promtBillAmount && !billAmount) return true;
    if (prefixes.length && (!selectedPrefix.value || !selectedPrefix.value?.length)) return true;
    return false;
  }

  if (!pinCode?.length) return true;

  return false;
});

watch(
  () => form.houseId,
  (newVal) => {
    if(newVal) {
      getHouse(newVal);
      return;
    }
    houseData.value = [];
  }
)
// [END] Computed

// [START] Methods
const handleAddAccount = async () => {
  if (stage.value === 1 && addAccountByIdentOptions.promtPinCode) {
    stage.value = 2;
    return;
  }
  isLoadingAddAccountByIdent.value = true;
  isSending.value = true;

  try {
    const mode = appSettings.accountSelectMode;

    if (form.ident?.length && mode === 'Ident') {
      const fullIdent = selectedPrefix.value
        ? selectedPrefix.value + form.ident
        : form.ident;

      const result = await addAccountByIdent({
        ...form,
        ident: fullIdent,
        pinCode: form.pinCode,
      });

      await store.dispatch('meters/fetchList');

      if (result.accounts?.length) {
        emits('addAccountByIdent');
        createPush({
          type: 'success',
          message: `${isLegalAccount ? t('contract') : t('personal_account')} №${fullIdent} ${t('connected')}!`,
          duration: 2000,
        });
        form.ident = null;
      } else {
        createPush({
          type: 'error',
          message: result.error,
          duration: 3000,
        });
      }
    } else if (mode === 'AddressAndLastName') {
      const result = await addAccountByIdent(form);

      if (result.accounts?.length) {
        emits('addAccountByIdent');
        selectedAccount.value = result.accounts.find(
          (item) =>
            item.houseId === form.houseId &&
            item.address.includes(`кв. ${form.premiseNumber}`)
        );

        createPush({
          type: 'success',
          message: `${isLegalAccount ? t('contract') : t('personal_account')} №${selectedAccount.value?.ident} ${t('connected')}!`,
          duration: 2000,
        });

        form.ident = null;
      } else {
        createPush({
          type: 'error',
          message: result.error,
          duration: 3000,
        });
      }
    }
  } catch (error) {
    createPush({
      type: 'error',
      message: String(error),
      duration: 3000,
    });
  } finally {
    stage.value = 1;
    isSending.value = false;
    isLoadingAddAccountByIdent.value = false;
  }
};

const getHouseList = async() => {
  const result = await fetchHouseList();
  houses.value = result.Data.map(item => ({
    ...item,
    value: item.ID,
    title: item.Address,

  }));
}

const getHouse = async(houseId) => {
  const result = await fetchHouse(houseId);
  houseData.value = result.Data.map(item => ({
    ...item,
    value: item.ID,
    title: item.Number,

  }));
}

getAccountPrefixes();

if (addAccountByIdentOptions?.promtBillAmount) {
  getClientBases();
}

if(appSettings.accountSelectMode === 'AddressAndLastName') {
  getHouseList();
}

// [END] Methods

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", {
    class: "add-ident-form",
    onSubmit: _withModifiers(handleAddAccount, ["prevent"])
  }, [
    (stage.value === 1)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          (_unref(appSettings).accountSelectMode === 'AddressAndLastName')
            ? (_openBlock(), _createBlock(SmInput, {
                key: 0,
                modelValue: form.inn,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.inn) = $event)),
                label: "ИНН",
                type: "text",
                class: "add-ident-form__input"
              }, null, 8, ["modelValue"]))
            : _createCommentVNode("", true),
          (_unref(appSettings).accountSelectMode === 'AddressAndLastName')
            ? (_openBlock(), _createBlock(SmInput, {
                key: 1,
                modelValue: form.lastName,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.lastName) = $event)),
                label: "Фамилия",
                type: "text",
                class: "add-ident-form__input"
              }, null, 8, ["modelValue"]))
            : _createCommentVNode("", true),
          (_unref(appSettings).accountSelectMode === 'AddressAndLastName')
            ? (_openBlock(), _createBlock(SmSelect, {
                key: 2,
                modelValue: form.houseId,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.houseId) = $event)),
                options: houses.value,
                search: "",
                label: "Адрес",
                class: "add-ident-form__select"
              }, null, 8, ["modelValue", "options"]))
            : _createCommentVNode("", true),
          (_unref(appSettings).accountSelectMode === 'AddressAndLastName')
            ? (_openBlock(), _createBlock(SmSelect, {
                key: 3,
                modelValue: form.premiseNumber,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.premiseNumber) = $event)),
                options: houseData.value,
                label: "Номер квартиры или помещения",
                class: "add-ident-form__select"
              }, null, 8, ["modelValue", "options"]))
            : _createCommentVNode("", true),
          (_unref(addAccountByIdentOptions).promtClientBaseID)
            ? (_openBlock(), _createBlock(SmSelect, {
                key: 4,
                modelValue: form.clientBaseID,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.clientBaseID) = $event)),
                options: _unref(clientBases),
                label: _unref(t)('add_ident_city'),
                class: "add-ident-form__select"
              }, null, 8, ["modelValue", "options", "label"]))
            : _createCommentVNode("", true),
          (_unref(appSettings).accountSelectMode === 'Ident')
            ? (_openBlock(), _createBlock(SmInput, {
                key: 5,
                modelValue: form.ident,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.ident) = $event)),
                label: identNumber.value,
                type: "text",
                hint: identHint.value,
                class: "add-ident-form__input"
              }, null, 8, ["modelValue", "label", "hint"]))
            : _createCommentVNode("", true),
          (_unref(prefixes).length)
            ? (_openBlock(), _createBlock(SmSelect, {
                key: 6,
                modelValue: selectedPrefix.value,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((selectedPrefix).value = $event)),
                options: _unref(prefixes),
                label: _unref(t)('add_ident_management_company'),
                class: "add-ident-form__select"
              }, null, 8, ["modelValue", "options", "label"]))
            : _createCommentVNode("", true),
          (_unref(addAccountByIdentOptions).promtBillAmount)
            ? (_openBlock(), _createBlock(SmInput, {
                key: 7,
                modelValue: form.billAmount,
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((form.billAmount) = $event)),
                modelModifiers: { number: true },
                label: addBillSumLabel.value,
                hint: _unref(t)('add_ident_bill_sum_hint'),
                placeholder: "1000",
                class: "add-ident-form__input"
              }, null, 8, ["modelValue", "label", "hint"]))
            : _createCommentVNode("", true)
        ], 64))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (_unref(addAccountByIdentOptions).promtPinCode)
            ? (_openBlock(), _createBlock(SmInput, {
                key: 0,
                modelValue: form.pinCode,
                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((form.pinCode) = $event)),
                label: promtPinCodeLabel.value,
                hint: promtPinCodeMessage.value,
                maskOptions: { mask: promtPinCodeMaskOptions.value },
                type: "text",
                class: "add-ident-form__input"
              }, null, 8, ["modelValue", "label", "hint", "maskOptions"]))
            : _createCommentVNode("", true)
        ], 64)),
    _createElementVNode("div", _hoisted_2, [
      _renderSlot(_ctx.$slots, "button"),
      _createVNode(SmButton, {
        nativeType: "submit",
        type: "primary",
        class: "add-ident-form__button",
        loading: isLoadingAddAccountByIdent.value,
        disabled: isDisabledButton.value
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('add_button')), 1)
        ]),
        _: 1
      }, 8, ["loading", "disabled"])
    ])
  ], 40, _hoisted_1))
}
}

})